import React from 'react'

import { intComma } from 'humanize-plus'
import { omit } from 'ramda'

import { EndUser, User } from '@driftt/api-types'
import { AvailabilityIF } from '@driftt/app-config/dist/utils/driftTypes'
import { Button } from '@driftt/tide-core'

import { toPercentString } from 'api/report/realTime/helpers'
import { EMPTY_DISPLAY } from 'utils/chatRatingEmojis'
import { keyBy } from 'utils/ramda'
import { trackEvent } from 'utils/tracking'

import Bolt from './components/Bolt'
import { DONUT_COLORS, MissedChatInfo, QualifiedBy } from './constants'
import { getConversationMessageLink } from './Shared/ConversationStream/utils'

export const renderTodaysConvosCell = (openConvos, closedConvos, styles) => (
  <div>
    <span>Open {intComma(openConvos)}</span>
    <br />
    <span className={styles}>Closed {closedConvos}</span>
  </div>
)

export const renderConversionRateCell = (
  metric,
  rate,
  styles,
  cellClick: () => void
) => {
  const onClick = (event) => {
    event.preventDefault()
    cellClick()
  }
  return metric === '--' || metric === '0' || metric === 0 ? (
    <div>
      <span>{metric}</span>
      <br />
      <span className={styles}>{toPercentString(rate)}</span>
    </div>
  ) : (
    <div>
      <a className="drilldownCell" href="" onClick={onClick}>
        {metric}
      </a>
      <br />
      <span className={styles}>{toPercentString(rate)}</span>
    </div>
  )
}

export const formatCell = (data, error) =>
  !error ? intComma(data) : EMPTY_DISPLAY

export const drilldownCell = (data, cellClick: () => void) => {
  const onClick = (event) => {
    event.preventDefault()
    cellClick()
  }
  return data === '--' || data === '0' || data === 0 ? (
    <span>{data}</span>
  ) : (
    <a className="drilldownCell" href="" onClick={onClick}>
      {data}
    </a>
  )
}

export const renderMissedChatOutcomeCell = (reason) => {
  const outcome = MissedChatInfo[reason]
  return (
    <div>
      <span style={{ display: 'flex', alignItems: 'center' }}>
        <div
          className="drilldown-label-color"
          style={{ background: outcome.bg }}
        />
        {outcome.label}
      </span>
    </div>
  )
}

export const renderCQLScoreCell = (score) => {
  return (
    <div>
      <span
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {Array(score).fill(<Bolt className="lightning-bolt" height={'15'} />)}
        <span className="cql-qualified-label">Qualified</span>
      </span>
    </div>
  )
}

export const renderQualifiedByCell = (author) => {
  let color = 'none'
  let label = author
  switch (author) {
    case QualifiedBy.AGENT:
      label = 'Teammate'
      color = DONUT_COLORS.agent
      break
    case QualifiedBy.BOT:
      label = 'Bot'
      color = DONUT_COLORS.bot
      break
    case QualifiedBy.NONPARTICIPANT:
      label = 'Non-participant'
      color = DONUT_COLORS.nonParticipant
      break
    default:
      break
  }
  return (
    <div>
      <span style={{ display: 'flex', alignItems: 'center' }}>
        <div className="drilldown-label-color" style={{ background: color }} />
        {label}
      </span>
    </div>
  )
}

const handleMissedConversationClick = (
  convoId,
  isSalesView,
  messageId,
  reason
) => {
  trackEvent('RTDBehavior', {
    event: 'View Conversation',
    from: 'Drilldown: missed convo',
    conversationId: convoId,
    missedReason: reason,
    view: isSalesView ? 'sales' : 'service',
  })
  window.open(getConversationMessageLink(convoId, messageId), '_blank')
}

const handleConversationClick = (convoId, isSalesView, source, messageId?) => {
  trackEvent('RTDBehavior', {
    event: 'View Conversation',
    from: `Drilldown: ${source}`,
    conversationId: convoId,
    view: isSalesView ? 'sales' : 'service',
  })
  window.open(getConversationMessageLink(convoId, messageId ?? null), '_blank')
}

export const renderGoToChatButton = (
  convoId,
  isSalesView,
  source,
  messageId?,
  reason?
) => (
  <div>
    <Button
      type="tertiary"
      target="_blank"
      onClick={() =>
        reason
          ? handleMissedConversationClick(
              convoId,
              isSalesView,
              messageId,
              reason
            )
          : handleConversationClick(convoId, messageId, source, isSalesView)
      }
    >
      View chat
    </Button>
  </div>
)

export const todaysConvosTotals = (data, styles) =>
  data.reduce(
    (acc, row) => {
      acc.OPEN += parseInt(row.data[1].totalValues.OPEN)
      acc.CLOSED += parseInt(row.data[1].totalValues.CLOSED)
      acc.content = renderTodaysConvosCell(acc.OPEN, acc.CLOSED, styles)
      return acc
    },
    { OPEN: 0, CLOSED: 0, content: '' }
  )

export const getTotals = (data, idxsToTotal, initial) =>
  data.reduce((acc, row) => {
    idxsToTotal.forEach((idx) => {
      acc.data[idx].value += parseInt(row.data[idx].value)
      acc.data[idx].content = intComma(acc.data[idx].value)
    })
    return acc
  }, initial)

export const getFilteredTableData = (tableData, availabilityFilter) =>
  tableData.filter((row) => {
    switch (true) {
      case availabilityFilter.online && !availabilityFilter.offline:
        return row.data[0].value.availability === AvailabilityIF.AVAILABLE
      case availabilityFilter.offline && !availabilityFilter.online:
        return row.data[0].value.availability === AvailabilityIF.OFFLINE
      default:
        return true
    }
  })

export const mergeCsatRatings = (repPerformance, repRatings) => {
  if (repPerformance && repRatings) {
    const ratingsByAgentId = keyBy(repRatings, 'agentId')

    return repPerformance.map((row) => {
      return {
        ...row,
        ...omit(['agentId'], ratingsByAgentId[row.user]),
      }
    })
  }
  return []
}

export const getKeyValueTotals = (data, team) => {
  return data.reduce((acc, [agentId, convos]): number => {
    if (team.members.find((m) => m.id === Number(agentId))) {
      acc += convos
    }
    return acc
  }, 0)
}

export const getConvoTotals = (data, team) => {
  return data.reduce(
    (acc, [agentId, convoData]): number => {
      if (team.members.find((m) => m.id === Number(agentId))) {
        acc['OPEN'] += convoData['OPEN'] ?? 0
        acc['CLOSED'] += convoData['CLOSED'] ?? 0
        acc['TOTAL'] += convoData['OPEN'] ?? 0
        acc['TOTAL'] += convoData['CLOSED'] ?? 0
      }
      return acc
    },
    { OPEN: 0, CLOSED: 0, TOTAL: 0 }
  )
}

export const getMissedConvoTotals = (data, team) => {
  return data.reduce((acc, [agentId, data]): number => {
    if (team.members.find((m) => m.id === Number(agentId))) {
      acc += data.TOTAL ?? 0
    }
    return acc
  }, 0)
}

export const getAvgTeamResponseTime = (data, team) => {
  const averageData = data.reduce(
    (acc, row): number => {
      if (team.members.find((m) => m.id === row.user)) {
        acc.NUMERATOR += row.avgResponseTime ?? 0
        acc.DENOMINATOR += row.avgResponseTime ? 1 : 0
        acc.AVG = acc.NUMERATOR / acc.DENOMINATOR
      }
      return acc
    },
    { NUMERATOR: 0, DENOMINATOR: 0, AVG: 0 }
  )
  return averageData.AVG
}

export const getUserDisplayName = (
  user?: EndUser | User,
  isAgent = false
): string =>
  user?.name ?? user?.email ?? (isAgent ? 'Teammate' : 'Site visitor')

export const getAgentsToShow = (teammates, allUsers) => {
  const filteredAgents = teammates.filter(
    (item, pos) => teammates.indexOf(item) === pos
  )
  return filteredAgents.length > 0 && filteredAgents[0]
    ? filteredAgents.map((id) => allUsers[id])
    : Object.values(allUsers)
}
